import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useSchoolYear } from "../context/SchoolYearContext";

export const CLASS_SETTINGS_UPDATED_EVENT = "skarbnik:class-settings-updated";

export function useExpenses() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedSchoolYearId, loading: schoolYearLoading } = useSchoolYear();

  useEffect(() => {
    if (schoolYearLoading) {
      setLoading(true);
      return;
    }

    if (!selectedSchoolYearId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    supabase
      .from("expenses")
      .select("*")
      .eq("school_year_id", selectedSchoolYearId)
      .order("purchase_date", { ascending: false })
      .then(({ data }) => { setData(data ?? []); setLoading(false); });
  }, [schoolYearLoading, selectedSchoolYearId]);

  return { data, loading };
}

export function usePlannedExpenses() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedSchoolYearId, loading: schoolYearLoading } = useSchoolYear();

  useEffect(() => {
    if (schoolYearLoading) {
      setLoading(true);
      return;
    }

    if (!selectedSchoolYearId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    supabase
      .from("planned_expenses")
      .select("*")
      .eq("school_year_id", selectedSchoolYearId)
      .order("planned_date", { ascending: true })
      .then(({ data }) => { setData(data ?? []); setLoading(false); });
  }, [schoolYearLoading, selectedSchoolYearId]);

  return { data, loading };
}

export function useCollections() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedSchoolYearId, loading: schoolYearLoading } = useSchoolYear();

  useEffect(() => {
    if (schoolYearLoading) {
      setLoading(true);
      return;
    }

    if (!selectedSchoolYearId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    supabase
      .from("collections")
      .select("*")
      .eq("school_year_id", selectedSchoolYearId)
      .order("created_at", { ascending: false })
      .then(({ data }) => { setData(data ?? []); setLoading(false); });
  }, [schoolYearLoading, selectedSchoolYearId]);

  return { data, loading };
}

export function useTeacherNotices() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedSchoolYearId, loading: schoolYearLoading } = useSchoolYear();

  useEffect(() => {
    if (schoolYearLoading) {
      setLoading(true);
      return;
    }

    if (!selectedSchoolYearId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    supabase
      .from("teacher_notices")
      .select("*")
      .eq("school_year_id", selectedSchoolYearId)
      .order("due_date", { ascending: true })
      .then(({ data }) => { setData(data ?? []); setLoading(false); });
  }, [schoolYearLoading, selectedSchoolYearId]);

  return { data, loading };
}

export function useClassSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("class_settings")
      .select("*")
      .single();
    setData(data);
    setLoading(false);
    return data;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    function handleSettingsUpdated(event) {
      if (event.detail) {
        setData(event.detail);
        setLoading(false);
        return;
      }

      refresh();
    }

    window.addEventListener(CLASS_SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
    return () => {
      window.removeEventListener(CLASS_SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
    };
  }, [refresh]);

  return { data, loading, refresh };
}
